/**
 * Public Sentiment site-evidence notices.
 *
 * Public Sentiment refreshes its evidence at enqueue, so a site-evidence build
 * in progress (or evidence that needs a rebuild) HOLDS the run instead of
 * failing it — see `src/lib/sentiment/sentimentEvidenceReadiness.ts` and the
 * store's `enqueueReportGenerationJob`. These notices are how the user learns
 * about the hold; the condition is never rendered as "Failed".
 *
 *   - waiting: the run starts automatically when the build finishes, while
 *              this page stays open (Cancel).
 *   - held:    the evidence needs a rebuild (Rebuild SED), or the automatic
 *              wait/retries ran out (Run again). Dismiss either way. Nothing
 *              was dispatched.
 *
 * Styling reuses the existing inline notice recipe (bordered token tint +
 * `type-meta`, as the progress strip's stalled note) and the shared Button.
 */
import React, { useMemo } from 'react';
import { AlertTriangle, Clock3 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useStore } from '../../store';
import {
  SENTIMENT_EVIDENCE_WAITING_NOTICE,
  latestSentimentEvidenceHoldForSite,
  sentimentEvidenceHoldIsCurrent,
  siteEvidenceBuildInProgress,
  type SentimentEvidenceHold,
} from '../../lib/sentiment/sentimentEvidenceReadiness';

export interface SentimentEvidenceNoticeProps {
  kind: 'waiting' | 'held';
  message: string;
  /** Optional lead-in, e.g. the site name in the global sidebar. */
  title?: string;
  onCancel?: () => void;
  onRebuild?: () => void;
  /** A build is already running: the rebuild action shows as in progress. */
  rebuilding?: boolean;
  /** Re-send the held request (holds whose automatic wait/retries ran out). */
  onRunAgain?: () => void;
  onDismiss?: () => void;
  className?: string;
  'data-testid'?: string;
}

export const SentimentEvidenceNotice: React.FC<SentimentEvidenceNoticeProps> = ({
  kind,
  message,
  title,
  onCancel,
  onRebuild,
  rebuilding = false,
  onRunAgain,
  onDismiss,
  className,
  'data-testid': testId = 'sentiment-evidence-notice',
}) => {
  const Icon = kind === 'waiting' ? Clock3 : AlertTriangle;
  const hasActions = Boolean(onCancel || onRebuild || onRunAgain || onDismiss);
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testId}
      data-kind={kind}
      className={cn(
        'rounded-sm border px-2 py-1.5 space-y-1.5',
        kind === 'waiting' ? 'border-primary/40 bg-primary/5' : 'border-warning/40 bg-warning/10',
        className,
      )}
    >
      <p
        className={cn(
          'flex items-start gap-1.5 type-meta leading-relaxed',
          kind === 'waiting' ? 'text-text-secondary' : 'text-warning',
        )}
      >
        <Icon className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
        <span>
          {title ? <span className="font-medium text-text-primary">{title}: </span> : null}
          {message}
        </span>
      </p>
      {hasActions ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {onRebuild ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRebuild}
              disabled={rebuilding}
              data-testid={`${testId}-rebuild`}
            >
              {rebuilding ? 'Rebuilding…' : 'Rebuild SED'}
            </Button>
          ) : null}
          {onRunAgain ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRunAgain}
              data-testid={`${testId}-run-again`}
            >
              Run again
            </Button>
          ) : null}
          {onCancel ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onCancel}
              data-testid={`${testId}-cancel`}
            >
              Cancel
            </Button>
          ) : null}
          {onDismiss ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onDismiss}
              data-testid={`${testId}-dismiss`}
            >
              Dismiss
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/** The Public Sentiment hold that still applies to `siteId`, or null. */
export function useSentimentEvidenceHold(siteId: string | undefined): SentimentEvidenceHold | null {
  // Optional chaining throughout: several host test harnesses stub the store
  // with a partial `siteRag`; a notice must never take its host down.
  const holds = useStore(s => s.siteRag?.sentimentEvidenceHolds);
  const site = useStore(s => (siteId ? s.siteRag?.sites?.find(candidate => candidate.id === siteId) : undefined));
  return useMemo(() => {
    if (!siteId) return null;
    const hold = latestSentimentEvidenceHoldForSite(holds, siteId);
    return hold && sentimentEvidenceHoldIsCurrent(hold, site) ? hold : null;
  }, [holds, site, siteId]);
}

/**
 * Held-run notice wired to the store: Rebuild SED (when a rebuild resolves it)
 * or Run again (when the automatic wait/retries ran out), plus Dismiss.
 */
export const SentimentEvidenceHoldNotice: React.FC<{
  hold: SentimentEvidenceHold;
  title?: string;
  className?: string;
  'data-testid'?: string;
}> = ({ hold, title, className, 'data-testid': testId }) => {
  const site = useStore(s => s.siteRag?.sites?.find(candidate => candidate.id === hold.siteId));
  const rebuilding = site ? siteEvidenceBuildInProgress(site) : false;
  return (
    <SentimentEvidenceNotice
      kind="held"
      message={hold.message}
      title={title}
      className={className}
      data-testid={testId ?? 'sentiment-evidence-hold'}
      rebuilding={rebuilding}
      // The existing store action behind every "Rebuild SED" toast CTA.
      onRebuild={hold.reason === 'sed-rebuild-required'
        ? () => { void useStore.getState().rebuildSiteSed(hold.siteId); }
        : undefined}
      onRunAgain={hold.exhausted
        ? () => { void useStore.getState().retrySentimentEvidenceHold(hold.id); }
        : undefined}
      onDismiss={() => useStore.getState().dismissSentimentEvidenceHold(hold.id)}
    />
  );
};

/** Waiting-run notice wired to the store's cancel. */
export const SentimentEvidenceWaitingNotice: React.FC<{
  runId?: string;
  onCancelRun?: (runId: string) => void;
  title?: string;
  className?: string;
  'data-testid'?: string;
}> = ({ runId, onCancelRun, title, className, 'data-testid': testId }) => (
  <SentimentEvidenceNotice
    kind="waiting"
    message={SENTIMENT_EVIDENCE_WAITING_NOTICE}
    title={title}
    className={className}
    data-testid={testId ?? 'sentiment-evidence-waiting'}
    onCancel={runId
      ? () => {
          if (onCancelRun) onCancelRun(runId);
          else void useStore.getState().cancelReportGenerationJob(runId);
        }
      : undefined}
  />
);

/**
 * Global sidebar list (SiteRagEngine), in the slot where "Generation Failed"
 * used to appear for this condition: every waiting Public Sentiment run and
 * every hold that still applies, labelled with its site.
 */
export const SentimentEvidenceSidebarNotices: React.FC<{ className?: string }> = ({ className }) => {
  const activeGenerations = useStore(s => s.siteRag?.activeGenerations);
  const holds = useStore(s => s.siteRag?.sentimentEvidenceHolds);
  const storeSites = useStore(s => s.siteRag?.sites);
  const entries = useMemo(() => {
    const sites = storeSites ?? [];
    const siteName = (siteId: string) => sites.find(site => site.id === siteId)?.name ?? 'Site';
    const waiting = Object.values(activeGenerations ?? {})
      .filter(run => run.waitingForSiteEvidence)
      .map(run => ({ kind: 'waiting' as const, id: run.id, title: siteName(run.siteId) }));
    const held = Object.values(holds ?? {})
      .filter(hold => sentimentEvidenceHoldIsCurrent(hold, sites.find(site => site.id === hold.siteId)))
      .map(hold => ({ kind: 'held' as const, id: hold.id, title: siteName(hold.siteId), hold }));
    return [...waiting, ...held];
  }, [activeGenerations, holds, storeSites]);
  if (entries.length === 0) return null;
  return (
    <div className={cn('space-y-2', className)} data-testid="sentiment-evidence-sidebar">
      {entries.map(entry => (entry.kind === 'waiting'
        ? (
          <SentimentEvidenceWaitingNotice
            key={`waiting-${entry.id}`}
            runId={entry.id}
            title={`${entry.title} · Public Sentiment`}
            data-testid={`sentiment-evidence-sidebar-waiting-${entry.id}`}
          />
        )
        : (
          <SentimentEvidenceHoldNotice
            key={`held-${entry.id}`}
            hold={entry.hold}
            title={`${entry.title} · Public Sentiment`}
            data-testid={`sentiment-evidence-sidebar-hold-${entry.id}`}
          />
        )))}
    </div>
  );
};
